import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { Modal, Dropdown } from "semantic-ui-react";
import { useToasts } from "react-toast-notifications";
import { usePageTranslations } from "hooks/usePageTranslations";
import { useLanguage } from "hooks/useLanguage";
import * as api from "constants/api";
import {
  SG_GET_DEBRIEF_PDF_REPORT_ORG_TEMPLATES,
  SG_CREATE_DEBRIEF_PDF_REPORT,
  SG_GET_DEBRIEF_PDF_REPORT,
  SG_GET_CULTURE_AUDIT_REPORTS,
  SG_DELETE_DEBRIEF_PDF_REPORT,
  SG_GET_DEBRIEF_SCHEDULES,
  SG_GET_ORGANIZATION_GROUP,
  SG_GET_DEBRIEF_PDF_REPORT_SHARED_WITH,
  SG_SHARE_DEBRIEF_PDF_REPORT_WITH_EMPLOYEE,
  SG_UNSHARE_DEBRIEF_PDF_REPORT_WITH_EMPLOYEE,
  CLEAR_DEBRIEF_PDF_REPORT,
} from "constants/actions";
import AnalyticsBreadcrumb from "../NewAnalytics/AnalyticsBreadcrumb";
import { reportIconNew } from "../../layouts/LeaderLayout/Icons";
import { SURVEY_TYPE } from "constants/surveys";
import { LOAD_EMPLOYEES } from "../../constants/actions";
import NoData from "assets/images/UI/no_data.svg";

const EMAIL_REGEX = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

const normalizeEmail = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim().toLowerCase();
};

const extractEmailsFromText = (text) => {
  const raw = String(text || "");
  const matches = raw.match(EMAIL_REGEX) || [];
  return matches.map(normalizeEmail).filter(Boolean);
};

const AutomatedReports = () => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const t = usePageTranslations("ProtoType/AutomatedReports/translations");
  const { language } = useLanguage();
  const groupShareScanTokenRef = useRef(0);
  const [view, setView] = useState("reports"); // "library" or "reports"
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [loadingCard, setLoadingCard] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [selectedOrganizationGroups, setSelectedOrganizationGroups] = useState([]);
  const [generatePasteEmailsText, setGeneratePasteEmailsText] = useState("");
  const [generatePasteEmailsResult, setGeneratePasteEmailsResult] = useState(null);
  const [groupManagerSearchTerm, setGroupManagerSearchTerm] = useState("");
  const [debouncedGroupManagerSearchTerm, setDebouncedGroupManagerSearchTerm] =
    useState("");
  const [reportsPage, setReportsPage] = useState(1);
  const [reportsPageSize] = useState(25);
  const [batchSendState, setBatchSendState] = useState({
    active: false,
    total: 0,
    sent: 0,
    items: [], // [{ groupId, label, status: "pending" | "sent" }]
  });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ isOpen: false, reportId: null, reportName: null });
  const [shareModal, setShareModal] = useState({ isOpen: false, report: null });
  const [shareEmployeeIds, setShareEmployeeIds] = useState([]);

  const [groupShareModalOpen, setGroupShareModalOpen] = useState(false);
  const [groupShareTargetEmployeeId, setGroupShareTargetEmployeeId] = useState(null);
  const [groupShareManagerGroupIds, setGroupShareManagerGroupIds] = useState([]);
  const [groupShareSearchTerm, setGroupShareSearchTerm] = useState("");
  const [groupShareAvailableReportsById, setGroupShareAvailableReportsById] = useState({});
  const [groupShareSelectedReportIds, setGroupShareSelectedReportIds] = useState([]);
  const [groupShareExistingSharedReportIds, setGroupShareExistingSharedReportIds] = useState([]);
  const [groupShareExistingSharesScan, setGroupShareExistingSharesScan] = useState({
    active: false,
    total: 0,
    done: 0,
    message: null,
    error: null,
  });
  const [groupShareLoading, setGroupShareLoading] = useState({
    mode: null, // "manager_groups" | "search" | null
    active: false,
    total: 0,
    done: 0,
    message: null,
  });
  const [groupShareSendState, setGroupShareSendState] = useState({
    active: false,
    total: 0,
    sent: 0,
    items: [], // [{ reportId, label, status: "pending" | "sent" }]
  });
  const [downloadBusyReportId, setDownloadBusyReportId] = useState(null);

  // Form state
  const [reportName, setReportName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDebriefSchedule, setSelectedDebriefSchedule] = useState(null);

  const {
    org_templates,
    org_templates_fetched,
    debrief_pdf_report,
    debrief_pdf_report_raw,
    fetched,
    created,
  } = useSelector(
    (state) => ({
      org_templates: state.debrief_pdf_report?.org_templates || [],
      org_templates_fetched: state.debrief_pdf_report?.org_templates_fetched || false,
      debrief_pdf_report_raw: state.debrief_pdf_report?.debrief_pdf_report_raw || null,
      debrief_pdf_report: Array.isArray(state.debrief_pdf_report?.debrief_pdf_report)
        ? state.debrief_pdf_report.debrief_pdf_report
        : [],
      fetched: state.debrief_pdf_report?.fetched || false,
      created: state.debrief_pdf_report?.created || false,
    }),
    shallowEqual
  );

  const {
    core_data,
    get_auth,
    get_organizations,
    get_debrief_schedules,
    employeeId,
    get_culture_audit_reports,
    outcomesQuestionsList,
    organizationId,
    organizationGroups,
    permissionGroups,
    employees,
    sharedWithByReportId,
    shareActionByReportId,
    userEmployee,
  } = useSelector(
    (state) => ({
      core_data: state.audit?.core_data,
      get_auth: state.auth,
      get_organizations: state.organizations,
      get_debrief_schedules: state.debrief_schedule?.debrief_schedule?.results || [],
      employeeId: Number(state.auth?.employee_id),
      get_culture_audit_reports: state.debrief_schedule?.culture_audit_reports,
      outcomesQuestionsList: state.surveyquestions?.outcome_questions || [],
      organizationId: Number(state.auth?.organization_id),
      organizationGroups: Array.isArray(state.organization_group?.organization_groups_by_type?.survey)
        ? state.organization_group.organization_groups_by_type.survey
        : Array.isArray(state.organization_group?.organization_groups)
        ? state.organization_group.organization_groups
        : [],
      permissionGroups: Array.isArray(state.organization_group?.organization_groups_by_type?.permission)
        ? state.organization_group.organization_groups_by_type.permission
        : [],
      employees: Array.isArray(state.employees?.employees) ? state.employees.employees : [],
      sharedWithByReportId: state.debrief_pdf_report?.shared_with_by_report_id || {},
      shareActionByReportId: state.debrief_pdf_report?.share_action_by_report_id || {},
      // Admin detection matches app convention: account_type === 5
      userEmployee: state.employees?.userEmp || state.auth?.employee || null,
    }),
    shallowEqual
  );

  const organizationIdLegacy = get_auth?.organization_id;
  const resolvedOrganizationId = organizationId || Number(organizationIdLegacy);
  const isAdmin = Number(userEmployee?.account_type) === 5;
  const effectiveView = isAdmin ? view : "reports";

  const buildReportsQuery = useCallback(
    ({ groupManager, page, pageSize } = {}) => {
      const parts = ["report_type=3"];
      const term = String(groupManager || "").trim();
      if (isAdmin && term) {
        parts.push(`group_manager=${encodeURIComponent(term)}`);
      }
      const p = Number(page) > 0 ? Number(page) : 1;
      const ps = Number(pageSize) > 0 ? Number(pageSize) : 25;
      parts.push(`page=${encodeURIComponent(p)}`);
      parts.push(`page_size=${encodeURIComponent(ps)}`);
      return parts.join("&");
    },
    [isAdmin]
  );

  const GROUP_SHARE_SCAN_PAGE_SIZE = 500;
  const GROUP_SHARE_SCAN_MAX_REPORTS = 2500;
  const GROUP_SHARE_SHARED_WITH_CONCURRENCY = 6;

  // Debounce manager filter (admin-only) + reset pagination to page 1 on filter change
  useEffect(() => {
    if (!isAdmin) return;
    setReportsPage(1);
    const handle = setTimeout(() => {
      setDebouncedGroupManagerSearchTerm(String(groupManagerSearchTerm || "").trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [groupManagerSearchTerm, isAdmin]);

  // Fetch templates on mount
  useEffect(() => {
    if (!org_templates_fetched) {
      dispatch({ type: SG_GET_DEBRIEF_PDF_REPORT_ORG_TEMPLATES });
    }
  }, [dispatch, org_templates_fetched]);

  // Ensure debrief schedules are available for dropdown
  useEffect(() => {
    if (!resolvedOrganizationId) return;
    dispatch({
      type: SG_GET_DEBRIEF_SCHEDULES,
      payload: `page=1&page_size=500&type_of=${SURVEY_TYPE.AUDIT}&organization=${resolvedOrganizationId}`,
    });
  }, [dispatch, resolvedOrganizationId]);

  // Fetch generated reports
  useEffect(() => {
    if (effectiveView === "reports") {
      dispatch({
        type: SG_GET_DEBRIEF_PDF_REPORT,
        payload: buildReportsQuery({
          groupManager: debouncedGroupManagerSearchTerm,
          page: reportsPage,
          pageSize: reportsPageSize,
        }),
      });
    }
  }, [
    dispatch,
    effectiveView,
    buildReportsQuery,
    debouncedGroupManagerSearchTerm,
    reportsPage,
    reportsPageSize,
  ]);

  // Refresh reports after creation
  useEffect(() => {
    if (created) {
      addToast(t("toastReportQueuedRefreshing"), {
        appearance: "success",
        autoDismiss: true,
      });
      dispatch({
        type: SG_GET_DEBRIEF_PDF_REPORT,
        payload: buildReportsQuery({
          groupManager: debouncedGroupManagerSearchTerm,
          page: reportsPage,
          pageSize: reportsPageSize,
        }),
      });
      dispatch({ type: CLEAR_DEBRIEF_PDF_REPORT });
    }
  }, [
    dispatch,
    created,
    addToast,
    t,
    buildReportsQuery,
    debouncedGroupManagerSearchTerm,
    reportsPage,
    reportsPageSize,
  ]);

  // Ensure engagement (culture audit) reports are available to derive survey series
  useEffect(() => {
    if (!resolvedOrganizationId) return;
    const hasReports = Array.isArray(get_culture_audit_reports?.response) && get_culture_audit_reports.response.length > 0;
    if (hasReports) return;
    dispatch({
      type: SG_GET_CULTURE_AUDIT_REPORTS,
      payload: `audit-type-id=${SURVEY_TYPE.AUDIT}&organization=${resolvedOrganizationId}`,
    });
  }, [dispatch, resolvedOrganizationId, get_culture_audit_reports?.response]);

  // Ensure organization groups are available for naming
  useEffect(() => {
    if (!resolvedOrganizationId) return;
    if (organizationGroups?.length) return;
    dispatch({
      type: SG_GET_ORGANIZATION_GROUP,
      payload: {
        organization: resolvedOrganizationId,
        group_type: "survey",
        page: 1,
        page_size: 500,
      },
    });
  }, [dispatch, resolvedOrganizationId, organizationGroups?.length]);

  // Ensure permission (manager) groups are available for the "User(s)" dropdown
  useEffect(() => {
    if (!resolvedOrganizationId) return;
    dispatch({
      type: SG_GET_ORGANIZATION_GROUP,
      payload: {
        organization: resolvedOrganizationId,
        group_type: "permission",
        page: 1,
        page_size: 5000,
      },
    });
  }, [dispatch, resolvedOrganizationId]);

  // If the user navigates elsewhere (e.g. User Management) and comes back without unmounting,
  // the shared org-group Redux slice can get repopulated with a small page_size list (12).
  // Refresh the full list whenever the Generate modal is opened so search is always complete.
  useEffect(() => {
    if (!resolvedOrganizationId) return;
    if (!showGenerateModal) return;
    dispatch({
      type: SG_GET_ORGANIZATION_GROUP,
      payload: {
        organization: resolvedOrganizationId,
        group_type: "permission",
        page: 1,
        page_size: 5000,
      },
    });
  }, [dispatch, resolvedOrganizationId, showGenerateModal]);

  // Get categories from core_data
  const categories = core_data?.categories?.[0]?.categories || [];

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setShowGenerateModal(true);
    setReportName("");
    setSelectedCategory(null);
    setSelectedDebriefSchedule(null);
    setSelectedOrganizationGroups([]);
    setBatchSendState({ active: false, total: 0, sent: 0, items: [] });
  };

  // Helpers to normalize ids and names (match NewAnalytics)
  const extractNumericId = (value) => {
    if (value == null) return null;
    if (Array.isArray(value)) {
      for (const item of value) {
        const id = extractNumericId(item);
        if (id != null) return id;
      }
      return null;
    }
    if (typeof value === "object") {
      if ("id" in value) return extractNumericId(value.id);
      if ("pk" in value) return extractNumericId(value.pk);
      if ("value" in value) return extractNumericId(value.value);
      return null;
    }
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) {
      return numeric;
    }
    return null;
  };

  const pickFirstNumericId = (...candidates) => {
    for (const candidate of candidates) {
      const id = extractNumericId(candidate);
      if (id != null) return id;
    }
    return null;
  };

  const extractDisplayName = (value) => {
    if (value == null) return null;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed ? trimmed : null;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        const name = extractDisplayName(item);
        if (name) return name;
      }
      return null;
    }
    if (typeof value === "object") {
      const candidates = [
        value.name,
        value.title,
        value.group_name,
        value.groupName,
        value.team_name,
        value.display_name,
        value.displayName,
        value.label,
      ];
      for (const candidate of candidates) {
        const name = extractDisplayName(candidate);
        if (name) return name;
      }
    }
    return null;
  };

  const pickFirstDisplayName = (...candidates) => {
    for (const candidate of candidates) {
      const name = extractDisplayName(candidate);
      if (name) return name;
    }
    return null;
  };

  // Build maps for schedule/group metadata (align with NewAnalytics)
  const scheduleMapById = useMemo(() => {
    return (get_debrief_schedules || []).reduce((map, sched) => {
      const schedId = extractNumericId(sched?.id);
      if (schedId != null) {
        map.set(schedId, sched);
      }
      return map;
    }, new Map());
  }, [get_debrief_schedules]);

  const organizationGroupNameMap = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(organizationGroups) ? organizationGroups : [];
    list.forEach((group) => {
      const id = Number(group?.id);
      if (Number.isFinite(id) && id > 0) {
        const name = group?.name || group?.title || group?.group_name || group?.team_name;
        if (name) {
          map.set(id, name);
        }
      }
    });
    const responses = Array.isArray(get_culture_audit_reports?.response)
      ? get_culture_audit_reports.response
      : [];
    responses.forEach((response) => {
      const groupObj = response?.organization_group;
      const id = pickFirstNumericId(
        groupObj?.id,
        response?.organization_group_id,
        response?.group_id,
        response?.team_id
      );
      if (id == null) return;
      const name =
        response?.name ||
        groupObj?.name ||
        response?.organization_group_name ||
        response?.group_name ||
        response?.team_name ||
        response?.name;
      if (name) {
        map.set(id, name);
      }
    });
    return map;
  }, [organizationGroups, get_culture_audit_reports?.response]);

  const scheduleToGroupIdMap = useMemo(() => {
    const map = new Map();
    (get_debrief_schedules || []).forEach((sched) => {
      const schedId = extractNumericId(sched?.id);
      const groupId = sched?.organization_group;
      if (schedId == null || groupId == null) return;
      const groupNameFromMap = organizationGroupNameMap.get(groupId);
      const groupName = groupNameFromMap || sched?.group_name || null;
      map.set(schedId, { groupId, groupName });
    });
    const responses = Array.isArray(get_culture_audit_reports?.response)
      ? get_culture_audit_reports.response
      : [];
    responses.forEach((response) => {
      const schedId = pickFirstNumericId(
        response?.debrief_schedule,
        response?.debrief_schedule_id,
        response?.schedule,
        response?.schedule_id,
        response?.survey_schedule,
        response?.survey_schedule_id,
        response?.survey_event,
        response?.survey_event_id
      );
      const groupObj = response?.organization_group;
      const groupId = pickFirstNumericId(
        groupObj?.id,
        response?.organization_group_id,
        response?.group,
        response?.group_id,
        response?.team,
        response?.team_id,
        response?.organization_group_details?.id
      );
      if (schedId == null || groupId == null) return;
      const existing = map.get(schedId);
      const groupNameFromMap = organizationGroupNameMap.get(groupId);
      const candidateName =
        groupNameFromMap ||
        existing?.groupName ||
        groupObj?.name ||
        response?.organization_group_name ||
        response?.group_name ||
        response?.team_name ||
        response?.name ||
        null;
      map.set(schedId, { groupId, groupName: candidateName });
    });
    return map;
  }, [get_debrief_schedules, get_culture_audit_reports?.response, organizationGroupNameMap]);

  // Map resolved debrief_schedule id -> { questionSortOrder, outcomeSortOrder, surveyName } (align keys to schedule ids)
  const scheduleSortOrdersById = useMemo(() => {
    const map = new Map();
    const responses = Array.isArray(get_culture_audit_reports?.response) ? get_culture_audit_reports.response : [];
    responses.forEach((item) => {
      const responseId = extractNumericId(item?.id);
      const directSchedule = responseId != null ? scheduleMapById.get(responseId) : null;
      const fallbackScheduleId = pickFirstNumericId(
        item?.debrief_schedule,
        item?.debrief_schedule_id,
        item?.schedule,
        item?.schedule_id,
        item?.survey_schedule,
        item?.survey_schedule_id,
        item?.survey_event,
        item?.survey_event_id
      );
      const schedId = directSchedule ? responseId : fallbackScheduleId;
      if (schedId == null) return;
      const questionSortOrder = item?.question_sort_order || item?.question?.sort_order || null;
      const outcomeSortOrder = item?.additional_questions?.outcome_question?.question?.sort_order || null;
      const surveyName = item?.question?.name || item?.name || null;
      if (!map.has(schedId)) {
        map.set(schedId, { questionSortOrder, outcomeSortOrder, surveyName });
      }
    });
    return map;
  }, [get_culture_audit_reports?.response, scheduleMapById]);

  // Build dropdown options from debrief history (align with NewAnalytics history list)
  const debriefHistoryOptions = useMemo(() => {
    const responses = Array.isArray(get_culture_audit_reports?.response) ? [...get_culture_audit_reports.response] : [];
    if (responses.length === 0) return [];
    // Sort newest first
    responses.sort((a, b) => (extractNumericId(b?.id) || 0) - (extractNumericId(a?.id) || 0));
    const bySchedule = new Map();
    responses.forEach((response) => {
      const sortOrder = response?.question?.sort_order;
      const responseId = extractNumericId(response?.id);
      const directSchedule = responseId != null ? scheduleMapById.get(responseId) : null;
      const closeDate = directSchedule?.close_at || response?.date || null;
      const fallbackScheduleId = pickFirstNumericId(
        response?.debrief_schedule,
        response?.debrief_schedule_id,
        response?.schedule,
        response?.schedule_id,
        response?.survey_schedule,
        response?.survey_schedule_id,
        response?.survey_event,
        response?.survey_event_id
      );
      const resolvedDebriefScheduleId = directSchedule ? responseId : fallbackScheduleId;
      if (resolvedDebriefScheduleId == null) return;
      const scheduleGroupMeta = scheduleToGroupIdMap.get(resolvedDebriefScheduleId) || null;
      const scheduleGroupId = scheduleGroupMeta?.groupId ?? null;
      const groupNameFromMap = scheduleGroupId != null ? organizationGroupNameMap.get(scheduleGroupId) : null;
      const organizationGroupName = pickFirstDisplayName(
        response?.name,
        groupNameFromMap,
        scheduleGroupMeta?.groupName,
        directSchedule?.organization_group,
        directSchedule?.group_name,
        response?.organization_group_name,
        response?.organization_group,
        response?.group,
        response?.team,
        response?.group_name,
        response?.team_name,
        sortOrder ? t("fallbackGroupNumber", { id: sortOrder }) : t("fallbackGroup")
      );
      const questionTitle =
        response?.question?.name ||
        (sortOrder ? t("fallbackSurveyNumber", { id: sortOrder }) : t("fallbackSurvey"));
      const primaryLabel = organizationGroupName || questionTitle;
      const formattedDate = closeDate ? new Date(closeDate).toLocaleDateString() : "N/A";
      const existing = bySchedule.get(resolvedDebriefScheduleId);
      const dt = closeDate ? new Date(closeDate) : new Date(0);
      // Keep the latest entry per schedule id
      if (!existing || (existing._dateObj || new Date(0)) < dt) {
        bySchedule.set(resolvedDebriefScheduleId, {
          value: resolvedDebriefScheduleId,
          label: primaryLabel,
          formattedDate,
          _dateObj: dt,
        });
      }
    });
    // Return sorted by date desc
    return Array.from(bySchedule.values()).sort((a, b) => (b._dateObj || 0) - (a._dateObj || 0));
  }, [get_culture_audit_reports?.response, scheduleToGroupIdMap, organizationGroupNameMap, scheduleMapById, t]);

  const handleGenerateReport = async () => {
    if (!selectedTemplate || !reportName || !selectedCategory || !selectedDebriefSchedule) {
      return;
    }

    // Infer sort orders from the selected debrief schedule
    const schedMeta = scheduleSortOrdersById.get(Number(selectedDebriefSchedule)) || {};
    const inferredQuestionSortOrder = schedMeta.questionSortOrder;
    const inferredOutcomeSortOrder = schedMeta.outcomeSortOrder;

    setGenerating(true);
    setLoadingCard(selectedTemplate.id);

    const basePayload = {
      employee: employeeId,
      organization: resolvedOrganizationId,
      debrief_schedule: selectedDebriefSchedule,
      report_type: 3,
      status: 5,
      pdf_template_report: selectedTemplate.id,
      report_name: reportName,
      // Move ancillary params under additional_info
      additional_info: {
        parent_anchor_id: selectedCategory,
        ...(inferredQuestionSortOrder != null && {
          survey_question_sort_order: Number(inferredQuestionSortOrder),
        }),
        ...(inferredOutcomeSortOrder != null && {
          outcome_question_sort_order: Number(inferredOutcomeSortOrder),
        }),
      },
    };

    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      const groupIds = Array.isArray(selectedOrganizationGroups)
        ? selectedOrganizationGroups.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
        : [];

      if (!groupIds.length) {
        dispatch({ type: SG_CREATE_DEBRIEF_PDF_REPORT, payload: basePayload });
      } else {
        const groupLabelMap = new Map();
        (permissionGroups || []).forEach((g) => {
          const groupId = Number(g?.id);
          if (!Number.isFinite(groupId) || groupId <= 0) return;
          const mgr = g?.manager;
          const mgrName =
            mgr && typeof mgr === "object"
              ? `${mgr.first_name || ""} ${mgr.last_name || ""}`.trim() || mgr.email
              : null;
          // Prefer explicit group naming over manager identity (matches AccessV2 GroupDetail behavior)
          const label = g?.name || g?.title || mgrName || t("fallbackGroupNumber", { id: groupId });
          groupLabelMap.set(groupId, label);
        });

        const initialItems = groupIds.map((id) => ({
          groupId: id,
          label: groupLabelMap.get(id) || t("fallbackGroupNumber", { id }),
          status: "pending",
        }));

        setBatchSendState({ active: true, total: groupIds.length, sent: 0, items: initialItems });

        for (let idx = 0; idx < groupIds.length; idx += 1) {
          const groupId = groupIds[idx];
          const managerLabel = groupLabelMap.get(groupId) || t("fallbackGroupNumber", { id: groupId });
          const combinedReportName = `${managerLabel} - ${reportName}`.trim();
          dispatch({
            type: SG_CREATE_DEBRIEF_PDF_REPORT,
            payload: {
              ...basePayload,
              organization_group: groupId,
              report_name: combinedReportName,
            },
          });

          setBatchSendState((prev) => {
            const nextItems = (prev.items || []).map((item) =>
              item.groupId === groupId ? { ...item, status: "sent" } : item
            );
            return {
              ...prev,
              active: true,
              sent: Math.min(prev.sent + 1, prev.total || groupIds.length),
              items: nextItems,
            };
          });

          // Yield to the UI so progress updates are visible
          // (This is "send progress", not API completion tracking)
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }

      setShowGenerateModal(false);
      setView("reports");
      addToast(t("toastReportGenerationStarted"), {
        appearance: "success",
        autoDismiss: true,
      });
      setSelectedTemplate(null);
      setReportName("");
      setSelectedCategory(null);
      setSelectedDebriefSchedule(null);
      setSelectedOrganizationGroups([]);
      setGeneratePasteEmailsText("");
      setGeneratePasteEmailsResult(null);
      setBatchSendState({ active: false, total: 0, sent: 0, items: [] });
    } catch (error) {
      console.error("Error generating report:", error);
    } finally {
      setGenerating(false);
      setLoadingCard(null);
    }
  };

  // Build Engagement survey series (by sort_order) from culture audit reports
  const engagementSeriesOptions = useMemo(() => {
    const responses = Array.isArray(get_culture_audit_reports?.response) ? get_culture_audit_reports.response : [];
    if (responses.length === 0) return [];
    const grouped = responses.reduce((acc, response) => {
      const sortOrder = response?.question?.sort_order;
      if (!sortOrder) return acc;
      if (!acc[sortOrder]) {
        acc[sortOrder] = {
          value: sortOrder,
          label: response?.question?.name || t("fallbackSurveyNumber", { id: sortOrder }),
        };
      }
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => a.value - b.value);
  }, [get_culture_audit_reports?.response, t]);

  // We no longer render outcome selection; it's inferred from selected schedule

  // Dropdown options for permission (manager) groups. Search is client-side over this list.
  // We intentionally load a large page_size (5000) like AccessV2 and let the dropdown filter by text.
  const permissionGroupOptions = useMemo(() => {
    const list = Array.isArray(permissionGroups) ? permissionGroups : [];
    return list
      .map((g) => {
        const id = Number(g?.id);
        if (!Number.isFinite(id) || id <= 0) return null;

        const mgr = g?.manager;
        const managerName =
          mgr && typeof mgr === "object"
            ? `${mgr.first_name || ""} ${mgr.last_name || ""}`.trim()
            : "";
        const managerEmail =
          mgr && typeof mgr === "object" ? String(mgr.email || "").trim() : "";

        const groupName = String(g?.name || g?.title || "").trim();
        const primaryLabel = String(groupName || managerName || managerEmail || "").trim();
        // Semantic UI Dropdown search matches against `text`.
        // Include group name, manager name, AND email so users can search by any of them.
        const searchBlob = [groupName, managerName, managerEmail].filter(Boolean).join(" ").trim();
        const text = searchBlob || primaryLabel || t("fallbackGroupNumber", { id });

        return { key: id, value: id, text };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.text).localeCompare(String(b.text)));
  }, [permissionGroups, t]);

  const permissionGroupsByManagerEmail = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(permissionGroups) ? permissionGroups : [];
    list.forEach((g) => {
      const groupId = Number(g?.id);
      if (!Number.isFinite(groupId) || groupId <= 0) return;
      const email = normalizeEmail(g?.manager?.email);
      if (!email) return;
      const existing = map.get(email);
      if (Array.isArray(existing)) {
        existing.push(groupId);
      } else {
        map.set(email, [groupId]);
      }
    });
    return map;
  }, [permissionGroups]);

  const handleApplyGeneratePasteEmails = () => {
    const emails = extractEmailsFromText(generatePasteEmailsText);
    const uniqueEmails = Array.from(new Set(emails));
    if (!uniqueEmails.length) {
      setGeneratePasteEmailsResult({
        totalUnique: 0,
        matchedEmails: 0,
        unmatched: 0,
        alreadySelected: 0,
        unmatchedEmails: [],
      });
      return;
    }

    const unmatchedEmails = [];
    const matchedGroupIds = [];

    uniqueEmails.forEach((email) => {
      const groupIds = permissionGroupsByManagerEmail.get(email);
      if (Array.isArray(groupIds) && groupIds.length > 0) {
        matchedGroupIds.push(...groupIds);
      } else {
        unmatchedEmails.push(email);
      }
    });

    const prevIds = Array.isArray(selectedOrganizationGroups)
      ? selectedOrganizationGroups.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
      : [];
    const nextSet = new Set(prevIds);
    const alreadySelected = matchedGroupIds.filter((id) => nextSet.has(id)).length;
    matchedGroupIds.forEach((id) => nextSet.add(id));

    setSelectedOrganizationGroups(Array.from(nextSet));
    setGeneratePasteEmailsResult({
      totalUnique: uniqueEmails.length,
      matchedEmails: Math.max(0, uniqueEmails.length - unmatchedEmails.length),
      unmatched: unmatchedEmails.length,
      alreadySelected,
      unmatchedEmails,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  const getTranslatedStatusText = (report) => {
    const raw = String(report?.status_text || "").trim();
    if (!raw) return t("unknownLabel");

    // Prefer mapping by status code when available.
    const statusCode = Number(report?.status);
    if (statusCode === 5) return t("statusQueuedForProcessing");

    // Fallback mapping by backend status_text (case-insensitive).
    const normalized = raw.toLowerCase();
    if (normalized === "queued for processing") return t("statusQueuedForProcessing");
    if (normalized === "completed") return t("statusCompleted");

    // If the backend returns other status strings, show them as-is.
    return raw;
  };

  const handleDeleteClick = (reportId, reportName) => {
    if (!isAdmin) return;
    setDeleteConfirmModal({ isOpen: true, reportId, reportName });
  };

  const handleDeleteConfirm = async () => {
    if (!isAdmin) return;
    if (deleteConfirmModal.reportId) {
      const reportName = deleteConfirmModal.reportName || t("deleteConfirmFallbackName");
      const reportId = deleteConfirmModal.reportId;
      
      // Close modal first
      setDeleteConfirmModal({ isOpen: false, reportId: null, reportName: null });
      
      // Dispatch delete action (saga will handle API call)
      dispatch({ type: SG_DELETE_DEBRIEF_PDF_REPORT, payload: reportId });
      
      // Show toast immediately
      addToast(t("toastReportDeleted", { name: reportName }), {
        appearance: "success",
        autoDismiss: true,
      });
      
      // Refresh the table after a short delay to ensure API call completes
      // The reducer will remove the item immediately when DELETE_DEBRIEF_PDF_REPORT is dispatched
      setTimeout(() => {
        dispatch({
          type: SG_GET_DEBRIEF_PDF_REPORT,
          payload: buildReportsQuery({
            groupManager: debouncedGroupManagerSearchTerm,
            page: reportsPage,
            pageSize: reportsPageSize,
          }),
        });
      }, 800);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmModal({ isOpen: false, reportId: null, reportName: null });
  };

  const getPrimaryDownloadFile = (report) => {
    const list = Array.isArray(report?.download_files) ? report.download_files : [];
    if (!list.length) return null;
    const pdf = list.find((f) => (f?.type || "").toLowerCase() === "pdf") || null;
    return pdf || list[0] || null;
  };

  const triggerBrowserDownload = (url, fallbackName = "report.pdf") => {
    if (!url) return;
    try {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = fallbackName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleDownloadReport = async (report) => {
    const reportId = report?.id;
    if (!reportId) return;
    if (downloadBusyReportId === reportId) return;

    const file = getPrimaryDownloadFile(report);
    const fileName = file?.file_name;

    if (!fileName) {
      addToast(t("downloadNoFileAvailable"), {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    setDownloadBusyReportId(reportId);
    try {
      const resp = await api.GET_DEBRIEF_PDF_REPORT_AWS_DOWNLOAD_URL({
        file_name: fileName,
      });
      const url = resp?.data?.url;
      if (!url) {
        throw new Error("Missing url from download endpoint");
      }
      triggerBrowserDownload(url, fileName);
    } catch (e) {
      // Fallback: if backend still uses the older secure_aws_download_url endpoint
      try {
        const resp = await api.GET_DEBRIEF_PDF_REPORT_URL({
          file_name: fileName,
        });
        const url = resp?.data?.url;
        if (!url) {
          throw new Error("Missing url from fallback endpoint");
        }
        triggerBrowserDownload(url, fileName);
      } catch (fallbackError) {
        addToast(t("downloadUnable"), {
          appearance: "error",
          autoDismiss: true,
        });
      }
    } finally {
      setDownloadBusyReportId(null);
    }
  };
  
  const openShareModal = (report) => {
    if (!isAdmin) return;
    if (!report?.id) return;
    setShareEmployeeIds([]);
    setShareModal({ isOpen: true, report });
    // Load login-capable employees list if needed
    if (!employees?.length) {
      dispatch({ type: LOAD_EMPLOYEES });
    }
    dispatch({ type: SG_GET_DEBRIEF_PDF_REPORT_SHARED_WITH, payload: { id: report.id } });
  };

  const closeShareModal = () => {
    setShareEmployeeIds([]);
    setShareModal({ isOpen: false, report: null });
  };

  const openGroupShareModal = () => {
    if (!isAdmin) return;
    setGroupShareModalOpen(true);
    setGroupShareTargetEmployeeId(null);
    setGroupShareManagerGroupIds([]);
    setGroupShareSearchTerm("");
    setGroupShareAvailableReportsById({});
    setGroupShareSelectedReportIds([]);
    setGroupShareExistingSharedReportIds([]);
    setGroupShareExistingSharesScan({
      active: false,
      total: 0,
      done: 0,
      message: null,
      error: null,
    });
    setGroupShareLoading({ mode: null, active: false, total: 0, done: 0, message: null });
    setGroupShareSendState({ active: false, total: 0, sent: 0, items: [] });
    if (!employees?.length) {
      dispatch({ type: LOAD_EMPLOYEES });
    }
  };

  const closeGroupShareModal = () => {
    if (groupShareSendState.active) return;
    setGroupShareModalOpen(false);
    setGroupShareTargetEmployeeId(null);
    setGroupShareManagerGroupIds([]);
    setGroupShareSearchTerm("");
    setGroupShareAvailableReportsById({});
    setGroupShareSelectedReportIds([]);
    setGroupShareExistingSharedReportIds([]);
    setGroupShareExistingSharesScan({
      active: false,
      total: 0,
      done: 0,
      message: null,
      error: null,
    });
    setGroupShareLoading({ mode: null, active: false, total: 0, done: 0, message: null });
    setGroupShareSendState((prev) => ({
      ...prev,
      active: false,
    }));
  };
 

  const renderIcon = (color) => {
    const c = color || "#64748b";
    return reportIconNew(c);
  };

  const palette = [
    { fg: "#2D70E2", bg: "#D5E2F9" },
    { fg: "#27CDA7", bg: "#D4F5ED" },
    { fg: "#F15062", bg: "#FCDCE0" },
    { fg: "#FEA711", bg: "#FFEDCF" },
  ];

  const loginEmployees = useMemo(() => {
    const list = Array.isArray(employees) ? employees : [];
    return list.filter((employee) => {
      const hasLoginFlag =
        employee.enable_login === true ||
        employee.enable_login === 1 ||
        employee.enable_login === "1";
      const hasPermissionsArray =
        Array.isArray(employee.new_permission) &&
        employee.new_permission.some(
          (p) =>
            (p?.name_id || p?.name) === "has_login" ||
            (p?.name_id || p?.name) === "hasLogin" ||
            (p?.value_id || p?.value) === "has_login"
        );
      const hasLoginAlt =
        employee.has_login === true ||
        employee.has_login === 1 ||
        employee.has_login === "1";
      return hasLoginFlag || hasPermissionsArray || hasLoginAlt;
    });
  }, [employees]);

  const loginEmployeeOptions = useMemo(() => {
    return (loginEmployees || [])
      .map((emp) => {
        const id = Number(emp?.id);
        if (!Number.isFinite(id) || id <= 0) return null;
        const name = `${emp?.first_name || ""} ${emp?.last_name || ""}`.trim();
        const text = name ? `${name} (${emp?.email || ""})` : emp?.email || `Employee ${id}`;
        return { key: id, value: id, text };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.text).localeCompare(String(b.text)));
  }, [loginEmployees]);

  const managerGroupOptions = useMemo(() => {
    const list = Array.isArray(permissionGroups) ? permissionGroups : [];
    return list
      .map((g) => {
        const id = Number(g?.id);
        if (!Number.isFinite(id) || id <= 0) return null;
        const mgr = g?.manager;
        const managerLabel =
          mgr && typeof mgr === "object"
            ? `${mgr.first_name || ""} ${mgr.last_name || ""}`.trim() || mgr.email
            : null;
        const email =
          mgr && typeof mgr === "object" ? String(mgr.email || "").trim() : "";
        const base = String(g?.name || g?.title || managerLabel || "").trim();
        const text = [base, email].filter(Boolean).join(" ").trim() || t("fallbackGroupNumber", { id });
        return { key: id, value: id, text };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.text).localeCompare(String(b.text)));
  }, [permissionGroups, t]);

  const groupShareAvailableReports = useMemo(() => {
    const values = Object.values(groupShareAvailableReportsById || {});
    return values
      .filter(Boolean)
      .sort((a, b) => {
        const dateA = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      });
  }, [groupShareAvailableReportsById]);

  const groupShareDiff = useMemo(() => {
    const selectedIds = Array.isArray(groupShareSelectedReportIds)
      ? groupShareSelectedReportIds.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
      : [];
    const existingIds = Array.isArray(groupShareExistingSharedReportIds)
      ? groupShareExistingSharedReportIds.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
      : [];
    const selectedSet = new Set(selectedIds);
    const existingSet = new Set(existingIds);
    const toShare = selectedIds.filter((id) => !existingSet.has(id));
    const toUnshare = existingIds.filter((id) => !selectedSet.has(id));
    return { toShare, toUnshare };
  }, [groupShareExistingSharedReportIds, groupShareSelectedReportIds]);

  const getReportDisplayName = (report) => {
    const id = report?.id;
    return report?.report_name || report?.org_template_name || t("fallbackReportNumber", { id });
  };

  const getReportTemplateLabel = (report) => {
    return (
      report?.org_template_name ||
      report?.template_name ||
      report?.pdf_template_report_name ||
      report?.pdf_template_report?.name ||
      (report?.pdf_template_report
        ? t("fallbackTemplateNumber", { id: report.pdf_template_report })
        : null) ||
      "—"
    );
  };

  const mergeReportsIntoGroupShareState = (reports) => {
    if (!Array.isArray(reports) || reports.length === 0) return;
    setGroupShareAvailableReportsById((prev) => {
      const next = { ...(prev || {}) };
      reports.forEach((r) => {
        const id = Number(r?.id);
        if (!Number.isFinite(id) || id <= 0) return;
        next[id] = r;
      });
      return next;
    });
  };

  const runWithConcurrencyLimit = async (items, limit, worker) => {
    const list = Array.isArray(items) ? items : [];
    const max = Math.max(1, Number(limit) || 1);
    let idx = 0;
    const runners = new Array(Math.min(max, list.length)).fill(null).map(async () => {
      while (idx < list.length) {
        const current = idx;
        idx += 1;
        // eslint-disable-next-line no-await-in-loop
        await worker(list[current], current);
      }
    });
    await Promise.all(runners);
  };

  const fetchReportsForGroupShare = async (queryString) => {
    const qs = queryString ? String(queryString) : "";
    const json = await api.GET_DEBRIEF_PDF_REPORT(qs);
    const data = json?.data;
    const list = Array.isArray(data) ? data : data?.results || [];
    return Array.isArray(list) ? list : [];
  };

  const fetchAllReportsForGroupShare = async ({ baseQuery, pageSize, maxTotal }) => {
    const size = Number(pageSize) > 0 ? Number(pageSize) : 500;
    const cap = Number(maxTotal) > 0 ? Number(maxTotal) : 2000;
    const collected = [];
    let page = 1;
    let hasNext = true;

    while (hasNext && collected.length < cap) {
      const qs = `${baseQuery}&page=${encodeURIComponent(page)}&page_size=${encodeURIComponent(size)}`;
      // eslint-disable-next-line no-await-in-loop
      const json = await api.GET_DEBRIEF_PDF_REPORT(qs);
      const data = json?.data;
      const results = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
      collected.push(...results);

      if (collected.length >= cap) {
        break;
      }

      const nextUrl = typeof data?.next === "string" ? data.next : null;
      if (nextUrl) {
        page += 1;
        hasNext = true;
      } else {
        hasNext = results.length === size;
        page += 1;
      }
    }

    return collected.slice(0, cap);
  };

  // When a target user is selected, discover which reports are explicitly shared with them
  useEffect(() => {
    if (!groupShareModalOpen) return;
    if (!isAdmin) return;

    const targetId = Number(groupShareTargetEmployeeId);
    if (!Number.isFinite(targetId) || targetId <= 0) {
      // Reset anything previously loaded for a prior target
      setGroupShareAvailableReportsById({});
      setGroupShareSelectedReportIds([]);
      setGroupShareLoading({ mode: null, active: false, total: 0, done: 0, message: null });
      setGroupShareExistingSharedReportIds([]);
      setGroupShareExistingSharesScan({
        active: false,
        total: 0,
        done: 0,
        message: null,
        error: null,
      });
      return;
    }

    // Reset selection + existing shares whenever the target changes
    // Also reset the loaded reports list so we don't mix state across targets
    setGroupShareAvailableReportsById({});
    setGroupShareSelectedReportIds([]);
    setGroupShareExistingSharedReportIds([]);
    setGroupShareLoading({ mode: null, active: false, total: 0, done: 0, message: null });

    const token = (groupShareScanTokenRef.current += 1);

    const run = async () => {
      try {
        setGroupShareExistingSharesScan({
          active: true,
          total: 0,
          done: 0,
          message: t("loadingCurrentAccess"),
          error: null,
        });

        const baseQuery = [
          "report_type=3",
          resolvedOrganizationId ? `organization=${encodeURIComponent(resolvedOrganizationId)}` : null,
          `employee_id=${encodeURIComponent(targetId)}`,
        ]
          .filter(Boolean)
          .join("&");

        const accessibleReports = await fetchAllReportsForGroupShare({
          baseQuery,
          pageSize: GROUP_SHARE_SCAN_PAGE_SIZE,
          maxTotal: GROUP_SHARE_SCAN_MAX_REPORTS,
        });

        if (groupShareScanTokenRef.current !== token) return;

        if (accessibleReports.length >= GROUP_SHARE_SCAN_MAX_REPORTS) {
          addToast(
            t("toastScanLimitReached", { max: GROUP_SHARE_SCAN_MAX_REPORTS }),
            { appearance: "warning", autoDismiss: true }
          );
        }

        // Ensure these reports are in the available list so they can be removed if needed
        mergeReportsIntoGroupShareState(accessibleReports);

        const reportIds = (accessibleReports || [])
          .map((r) => Number(r?.id))
          .filter((id) => Number.isFinite(id) && id > 0);

        setGroupShareExistingSharesScan({
          active: true,
          total: reportIds.length,
          done: 0,
          message: t("scanningExplicitShares"),
          error: null,
        });

        const sharedIdsSet = new Set();
        let done = 0;
        let failures = 0;
        let lastUiUpdate = Date.now();

        await runWithConcurrencyLimit(
          reportIds,
          GROUP_SHARE_SHARED_WITH_CONCURRENCY,
          async (reportId) => {
            if (groupShareScanTokenRef.current !== token) return;
            try {
              const resp = await api.GET_DEBRIEF_PDF_REPORT_SHARED_WITH(reportId);
              const list = resp?.data?.shared_with;
              const isShared =
                Array.isArray(list) &&
                list.some((it) => Number(it?.employee_id) === Number(targetId));
              if (isShared) sharedIdsSet.add(reportId);
            } catch (e) {
              failures += 1;
            } finally {
              done += 1;
              const now = Date.now();
              if (now - lastUiUpdate > 120 || done === reportIds.length) {
                lastUiUpdate = now;
                setGroupShareExistingSharesScan((prev) => {
                  if (!prev?.active) return prev;
                  return { ...prev, done: Math.min(done, reportIds.length) };
                });
              }
            }
          }
        );

        if (groupShareScanTokenRef.current !== token) return;

        const sharedIds = Array.from(sharedIdsSet.values());
        setGroupShareExistingSharedReportIds(sharedIds);
        setGroupShareSelectedReportIds((prev) => {
          const base = Array.isArray(prev) ? prev : [];
          const merged = new Set([...base, ...sharedIds]);
          return Array.from(merged.values());
        });

        if (failures > 0) {
          addToast(
            t("toastShareChecksFailed", { failures }),
            { appearance: "warning", autoDismiss: true }
          );
        }

        setGroupShareExistingSharesScan({
          active: false,
          total: reportIds.length,
          done: reportIds.length,
          message: null,
          error: null,
        });
      } catch (e) {
        if (groupShareScanTokenRef.current !== token) return;
        setGroupShareExistingSharesScan({
          active: false,
          total: 0,
          done: 0,
          message: null,
          error: e?.message || t("unableToLoadCurrentShares"),
        });
      }
    };

    run();
  }, [
    addToast,
    groupShareModalOpen,
    groupShareTargetEmployeeId,
    isAdmin,
    resolvedOrganizationId,
    t,
  ]);

  const handleLoadReportsFromManagers = async () => {
    const groupIds = Array.isArray(groupShareManagerGroupIds)
      ? groupShareManagerGroupIds.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
      : [];
    if (!groupIds.length) return;
    if (groupShareLoading.active || groupShareSendState.active) return;

    setGroupShareLoading({
      mode: "manager_groups",
      active: true,
      total: groupIds.length,
      done: 0,
      message: t("loadingReportsFromManagers"),
    });

    for (let idx = 0; idx < groupIds.length; idx += 1) {
      const gid = groupIds[idx];
      // eslint-disable-next-line no-await-in-loop
      const reports = await fetchReportsForGroupShare(
        `report_type=3&organization_group=${encodeURIComponent(gid)}&page=1&page_size=500`
      );
      mergeReportsIntoGroupShareState(reports);
      setGroupShareLoading((prev) => ({
        ...prev,
        done: Math.min((prev?.done || 0) + 1, groupIds.length),
      }));
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 30));
    }

    setGroupShareLoading({ mode: null, active: false, total: 0, done: 0, message: null });
  };

  const handleSearchReportsForGroupShare = async () => {
    const term = (groupShareSearchTerm || "").trim();
    if (!term) return;
    if (groupShareLoading.active || groupShareSendState.active) return;

    setGroupShareLoading({
      mode: "search",
      active: true,
      total: 1,
      done: 0,
      message: t("searchingReports"),
    });

    try {
      const reports = await fetchReportsForGroupShare(
        `report_type=3&report_name=${encodeURIComponent(term)}&page=1&page_size=500`
      );
      mergeReportsIntoGroupShareState(reports);
    } finally {
      setGroupShareLoading({ mode: null, active: false, total: 0, done: 0, message: null });
    }
  };

  const toggleGroupShareReportSelection = (reportId) => {
    const id = Number(reportId);
    if (!Number.isFinite(id) || id <= 0) return;
    setGroupShareSelectedReportIds((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    });
  };

  const handleGroupShareGivePermission = async () => {
    const targetId = Number(groupShareTargetEmployeeId);
    if (!Number.isFinite(targetId) || targetId <= 0) return;
    if (groupShareSendState.active) return;
    if (groupShareExistingSharesScan.active) return;

    const selectedIds = Array.isArray(groupShareSelectedReportIds)
      ? groupShareSelectedReportIds.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
      : [];
    const existingIds = Array.isArray(groupShareExistingSharedReportIds)
      ? groupShareExistingSharedReportIds.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
      : [];

    const selectedSet = new Set(selectedIds);
    const existingSet = new Set(existingIds);

    const toShare = selectedIds.filter((id) => !existingSet.has(id));
    const toUnshare = existingIds.filter((id) => !selectedSet.has(id));

    if (toShare.length === 0 && toUnshare.length === 0) return;

    const items = [
      ...toShare.map((rid) => ({
        action: "share",
        reportId: rid,
        label: getReportDisplayName(groupShareAvailableReportsById?.[rid] || { id: rid }),
        status: "pending",
      })),
      ...toUnshare.map((rid) => ({
        action: "unshare",
        reportId: rid,
        label: getReportDisplayName(groupShareAvailableReportsById?.[rid] || { id: rid }),
        status: "pending",
      })),
    ];

    setGroupShareSendState({
      active: true,
      total: items.length,
      sent: 0,
      items,
    });

    for (let idx = 0; idx < items.length; idx += 1) {
      const item = items[idx];
      if (!item?.reportId) continue;

      if (item.action === "unshare") {
        dispatch({
          type: SG_UNSHARE_DEBRIEF_PDF_REPORT_WITH_EMPLOYEE,
          payload: { id: item.reportId, employee_ids: [targetId] },
        });
      } else {
        dispatch({
          type: SG_SHARE_DEBRIEF_PDF_REPORT_WITH_EMPLOYEE,
          payload: { id: item.reportId, employee_ids: [targetId] },
        });
      }

      setGroupShareSendState((prev) => {
        const nextItems = (prev.items || []).map((it) =>
          it.reportId === item.reportId && it.action === item.action ? { ...it, status: "sent" } : it
        );
        return {
          ...prev,
          active: true,
          sent: Math.min((prev.sent || 0) + 1, prev.total || items.length),
          items: nextItems,
        };
      });

      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 30));
    }

    if (toShare.length > 0 && toUnshare.length > 0) {
      addToast(t("toastQueuedChanges", { shared: toShare.length, removed: toUnshare.length }), {
        appearance: "success",
        autoDismiss: true,
      });
    } else if (toShare.length > 0) {
      addToast(t("toastQueuedSharing", { count: toShare.length }), {
        appearance: "success",
        autoDismiss: true,
      });
    } else {
      addToast(t("toastQueuedRemoval", { count: toUnshare.length }), {
        appearance: "success",
        autoDismiss: true,
      });
    }

    // Optimistically update "existing" set so Apply Changes becomes disabled after queueing
    const nextExisting = new Set(existingIds);
    toUnshare.forEach((id) => nextExisting.delete(id));
    toShare.forEach((id) => nextExisting.add(id));
    setGroupShareExistingSharedReportIds(Array.from(nextExisting.values()));

    setGroupShareSendState({ active: false, total: 0, sent: 0, items: [] });
  };

  const activeShareReportId = shareModal?.report?.id || null;
  const activeSharedWithState = activeShareReportId != null ? sharedWithByReportId?.[activeShareReportId] : null;
  const activeShareActionState = activeShareReportId != null ? shareActionByReportId?.[activeShareReportId] : null;
  const sharedWithList = useMemo(() => {
    const data = activeSharedWithState?.data;
    const list = data?.shared_with;
    return Array.isArray(list) ? list : [];
  }, [activeSharedWithState?.data]);

  const handleShareSelected = () => {
    if (!activeShareReportId) return;
    const ids = Array.isArray(shareEmployeeIds)
      ? shareEmployeeIds.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
      : [];
    if (!ids.length) return;
    dispatch({
      type: SG_SHARE_DEBRIEF_PDF_REPORT_WITH_EMPLOYEE,
      payload: { id: activeShareReportId, employee_ids: ids },
    });
    setShareEmployeeIds([]);
  };

  const handleUnshareEmployee = (employeeId) => {
    if (!isAdmin) return;
    if (!activeShareReportId) return;
    const id = Number(employeeId);
    if (!Number.isFinite(id) || id <= 0) return;
    dispatch({
      type: SG_UNSHARE_DEBRIEF_PDF_REPORT_WITH_EMPLOYEE,
      payload: { id: activeShareReportId, employee_ids: [id] },
    });
  };

  return (
    <Container>
      <AnalyticsBreadcrumb />
      
      <Header>
        <HeaderTitle>
          {t("pageTitleMyReports")}
        </HeaderTitle>
        {isAdmin && (
        <SegmentedControl>
          <SegmentedTab
              active={view === "reports"}
              onClick={() => setView("reports")}
            isFirst
          >
              <HistoryIcon active={view === "reports"} />
              <span>{t("tabHistory")}</span>
          </SegmentedTab>
          <SegmentedTab
              active={view === "library"}
              onClick={() => setView("library")}
            isLast
          >
              <LibraryIcon active={view === "library"} />
              <span>{t("tabLibrary")}</span>
          </SegmentedTab>
        </SegmentedControl>
        )}
      </Header>

      {effectiveView === "library" && (
        <Section>
          <CardsGrid>
            {org_templates.map((template, idx) => {
              const scheme = {
                fg: palette[idx % palette.length].fg,
                bg: palette[idx % palette.length].bg,
              };
              const templateLabel =
                template.org_template_name ||
                template.template_name ||
                t("fallbackTemplateNumber", { id: template.id });
              return (
                <AnalyticsCard
                  key={template.id}
                  onClick={() => handleTemplateSelect(template)}
                  isLoading={loadingCard === template.id}
                  style={{ animationDelay: `${idx * 60}ms` }}
                >
                  <CardTop>
                    <IconBox bg={scheme.bg}>
                      <IconWrapper>{renderIcon(scheme.fg)}</IconWrapper>
                    </IconBox>
                    <CardTitle>{templateLabel}</CardTitle>
                  </CardTop>

                  <CardBody>
                    <CardDescription>
                      {template.global_template
                        ? t("libraryGlobalTemplate")
                        : template.org_template
                        ? t("libraryOrgTemplate")
                        : t("libraryTemplate")}
                    </CardDescription>
                    {template.params?.layout && (
                      <TemplateInfo>
                        <InfoItem>{t("libraryLayout", { layout: template.params.layout })}</InfoItem>
                        {template.params.sections && (
                          <InfoItem>
                            {t("librarySections", {
                              sections: Array.isArray(template.params.sections)
                                ? template.params.sections.join(", ")
                                : String(template.params.sections),
                            })}
                          </InfoItem>
                        )}
                      </TemplateInfo>
                    )}
                  </CardBody>

                  <CardFooter>
                    {loadingCard === template.id ? (
                      <LoadingSpinner />
                    ) : (
                      <OpenButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTemplateSelect(template);
                        }}
                        aria-label={`${t("generateReportCta")} ${templateLabel}`}
                      >
                        <span>{t("generateReportCta")}</span>
                        <ExploreArrow />
                      </OpenButton>
                    )}
                  </CardFooter>
                </AnalyticsCard>
              );
            })}
          </CardsGrid>
        </Section>
      )}

      {effectiveView === "reports" && (
        <Section>
          <>
            <HistoryTopBar>
              {isAdmin ? (
                <HistoryTopBarLeft>
                  <HistorySearchLabel>{t("historyManagerSearchLabel")}</HistorySearchLabel>
                  <HistorySearchInput
                    value={groupManagerSearchTerm}
                    onChange={(e) => setGroupManagerSearchTerm(e.target.value)}
                    placeholder={t("historyManagerSearchPlaceholder")}
                    aria-label={t("historyManagerSearchPlaceholder")}
                  />
                  <HistorySearchClearButton
                    type="button"
                    onClick={() => setGroupManagerSearchTerm("")}
                    disabled={!String(groupManagerSearchTerm || "").trim()}
                    title={t("historyManagerSearchClear")}
                  >
                    {t("historyManagerSearchClear")}
                  </HistorySearchClearButton>
                </HistoryTopBarLeft>
              ) : (
                <HistoryTopBarSpacer />
              )}
              <HistoryTopBarActions>
                {isAdmin && (
                  <HistoryTopBarButton onClick={openGroupShareModal}>
                    {t("historyGroupShare")}
                  </HistoryTopBarButton>
                )}
              </HistoryTopBarActions>
            </HistoryTopBar>

            <TableContainer>
              <Table>
              <TableHead>
                <TableHeaderRow>
                  <TableHeaderCell>{t("tableReportName")}</TableHeaderCell>
                  <TableHeaderCell>{t("tableTemplate")}</TableHeaderCell>
                  <TableHeaderCell>{t("tableCreatedAt")}</TableHeaderCell>
                  <TableHeaderCell>{t("tableStatus")}</TableHeaderCell>
                  <TableHeaderCell style={{ textAlign: "right" }}>{t("tableActions")}</TableHeaderCell>
                </TableHeaderRow>
              </TableHead>
              <TableBody>
                {debrief_pdf_report.length === 0 ? (
                  <TableRow>
                    <TableEmptyCell colSpan={5}>
                      <EmptyContent>
                        <EmptyIcon>
                          <img
                            src={NoData}
                            alt={t("emptyNoReportsAvailableAlt")}
                            style={{ width: 160 }}
                          />
                        </EmptyIcon>
                        <EmptyTitle>{t("emptyNoReportsAvailable")}</EmptyTitle>
                      </EmptyContent>
                    </TableEmptyCell>
                  </TableRow>
                ) : (
                  debrief_pdf_report.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>
                        <ReportNameText>
                          {report.org_template_name ||
                            report.report_name ||
                            t("fallbackReportNumber", { id: report.id })}
                        </ReportNameText>
                      </TableCell>
                      <TableCell>
                        <DateText>
                          {report.org_template_name ||
                            report.template_name ||
                            report.pdf_template_report_name ||
                            report.pdf_template_report?.name ||
                            (report.pdf_template_report
                              ? t("fallbackTemplateNumber", { id: report.pdf_template_report })
                              : "—")}
                        </DateText>
                      </TableCell>
                      <TableCell>
                        <DateText>{formatDate(report.created_at)}</DateText>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={report.status}>
                          {language === "fr" ? getTranslatedStatusText(report) : (report.status_text || t("unknownLabel"))}
                        </StatusBadge>
                      </TableCell>
                      <TableCell style={{ textAlign: "right" }}>
                        <ActionsCell>
                          <ActionButton
                            onClick={() => handleDownloadReport(report)}
                            disabled={
                              !getPrimaryDownloadFile(report)?.file_name ||
                              downloadBusyReportId === report.id
                            }
                          >
                            {downloadBusyReportId === report.id ? t("actionPreparing") : t("actionDownload")}
                          </ActionButton>
                          {isAdmin && (
                            <>
                          <ActionButton onClick={() => openShareModal(report)}>
                            {t("actionShare")}
                          </ActionButton>
                              <DeleteButton
                                onClick={() =>
                                  handleDeleteClick(
                                    report.id,
                                    report.org_template_name || report.report_name
                                  )
                                }
                              >
                            <DeleteIcon />
                          </DeleteButton>
                            </>
                          )}
                        </ActionsCell>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              </Table>
            </TableContainer>

              {(() => {
                const raw = debrief_pdf_report_raw;
                const totalCount = Array.isArray(raw)
                  ? raw.length
                  : Number(raw?.count || debrief_pdf_report.length || 0);
                const nextUrl = Array.isArray(raw)
                  ? null
                  : raw?.links?.next || raw?.next || null;
                const prevUrl = Array.isArray(raw)
                  ? null
                  : raw?.links?.previous || raw?.previous || null;
                const totalPages = Math.max(
                  1,
                  Math.ceil((totalCount || 0) / (reportsPageSize || 25))
                );
                const canPrev = reportsPage > 1 && !!prevUrl;
                const canNext = reportsPage < totalPages && !!nextUrl;

                // If backend returns no pagination metadata, don't show pager.
                if (!totalCount || totalPages <= 1) return null;

                return (
                  <ReportsPagination>
                    <ReportsPaginationInfo>
                      Showing{" "}
                      {totalCount
                        ? (reportsPage - 1) * reportsPageSize + 1
                        : 0}{" "}
                      to{" "}
                      {Math.min(reportsPage * reportsPageSize, totalCount)} of{" "}
                      {totalCount} reports
                    </ReportsPaginationInfo>
                    <ReportsPaginationControls>
                      <ReportsPaginationButton
                        type="button"
                        onClick={() =>
                          setReportsPage((p) => Math.max(1, Number(p) - 1))
                        }
                        disabled={!canPrev}
                      >
                        Previous
                      </ReportsPaginationButton>
                      <ReportsPaginationPage>
                        Page {reportsPage} of {totalPages}
                      </ReportsPaginationPage>
                      <ReportsPaginationButton
                        type="button"
                        onClick={() =>
                          setReportsPage((p) =>
                            Math.min(totalPages, Number(p) + 1)
                          )
                        }
                        disabled={!canNext}
                      >
                        Next
                      </ReportsPaginationButton>
                    </ReportsPaginationControls>
                  </ReportsPagination>
                );
              })()}
          </>
        </Section>
      )}

      <Modal
        onClose={() => setShowGenerateModal(false)}
        open={showGenerateModal}
        closeIcon={true}
      >
        <Modal.Header>
          <ModalTitle>{t("generateReportModalTitle")}</ModalTitle>
        </Modal.Header>
        <Modal.Content>
          {selectedTemplate && (
            <FormContainer>
              <FormField>
                <Label>{t("fieldTemplate")}</Label>
                <TemplateDisplay>
                  {selectedTemplate.org_template_name ||
                    selectedTemplate.template_name ||
                    t("fallbackTemplateNumber", { id: selectedTemplate.id })}
                </TemplateDisplay>
              </FormField>

              <FormField>
                <Label>{t("fieldReportNameRequired")}</Label>
                <Input
                  type="text"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  placeholder={t("placeholderReportName")}
                />
              </FormField>

              <FormField>
                <Label>{t("fieldCategoryRequired")}</Label>
                <Select
                  value={selectedCategory || ""}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">{t("selectCategoryPlaceholder")}</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField>
                <Label>{t("fieldDebriefScheduleRequired")}</Label>
                <Select
                  value={selectedDebriefSchedule || ""}
                  onChange={(e) => setSelectedDebriefSchedule(Number(e.target.value))}
                >
                  <option value="">{t("selectDebriefSchedulePlaceholder")}</option>
                  {debriefHistoryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {`${opt.label} - ${opt.formattedDate}`}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField>
                <Label>{t("fieldUsersOptional")}</Label>
                <div>
                  <ModalDropdown
                    multiple
                    search
                    selection
                    fluid
                    placeholder={t("placeholderSelectUsers")}
                    value={selectedOrganizationGroups}
                    options={permissionGroupOptions}
                    onChange={(e, { value }) => {
                      const next = Array.isArray(value) ? value : value ? [value] : [];
                      setSelectedOrganizationGroups(next);
                    }}
                    disabled={generating}
                  />
                </div>
                <PasteEmailsPanel>
                  <PasteEmailsHeaderRow>
                    <PasteEmailsTitle>{t("pasteEmailsTitle")}</PasteEmailsTitle>
                    <PasteEmailsActions>
                      <PasteEmailsSecondaryButton
                        type="button"
                        onClick={() => {
                          setGeneratePasteEmailsText("");
                          setGeneratePasteEmailsResult(null);
                        }}
                        disabled={!generatePasteEmailsText.trim() || generating}
                      >
                        {t("pasteEmailsClear")}
                      </PasteEmailsSecondaryButton>
                      <PasteEmailsPrimaryButton
                        type="button"
                        onClick={handleApplyGeneratePasteEmails}
                        disabled={!generatePasteEmailsText.trim() || generating}
                      >
                        {t("pasteEmailsApply")}
                      </PasteEmailsPrimaryButton>
                    </PasteEmailsActions>
                  </PasteEmailsHeaderRow>

                  <PasteEmailsHint>{t("pasteEmailsHint")}</PasteEmailsHint>

                  <PasteEmailsTextarea
                    value={generatePasteEmailsText}
                    onChange={(e) => setGeneratePasteEmailsText(e.target.value)}
                    placeholder={t("pasteEmailsPlaceholder")}
                    disabled={generating}
                  />

                  {generatePasteEmailsResult && (
                    <PasteEmailsResultBox
                      hasError={generatePasteEmailsResult.totalUnique > 0 && generatePasteEmailsResult.matchedEmails === 0}
                    >
                      <PasteEmailsResultLine>
                        {generatePasteEmailsResult.totalUnique === 0
                          ? t("pasteEmailsNoEmailsFound")
                          : t("pasteEmailsResultSummary", {
                              matched: generatePasteEmailsResult.matchedEmails,
                              total: generatePasteEmailsResult.totalUnique,
                              unmatched: generatePasteEmailsResult.unmatched,
                              alreadySelected: generatePasteEmailsResult.alreadySelected,
                            })}
                      </PasteEmailsResultLine>
                      {generatePasteEmailsResult.unmatchedEmails?.length > 0 && (
                        <PasteEmailsUnmatchedList>
                          {generatePasteEmailsResult.unmatchedEmails.slice(0, 12).map((email) => (
                            <PasteEmailsChip key={email}>{email}</PasteEmailsChip>
                          ))}
                          {generatePasteEmailsResult.unmatchedEmails.length > 12 && (
                            <PasteEmailsMoreText>
                              {t("pasteEmailsMoreUnmatched", {
                                count: generatePasteEmailsResult.unmatchedEmails.length - 12,
                              })}
                            </PasteEmailsMoreText>
                          )}
                        </PasteEmailsUnmatchedList>
                      )}
                    </PasteEmailsResultBox>
                  )}
                </PasteEmailsPanel>
              </FormField>

              {batchSendState.active && (
                <BatchProgress>
                  <BatchProgressTop>
                    <BatchProgressTitle>
                      {t("batchSendingProgress", { sent: batchSendState.sent, total: batchSendState.total })}
                    </BatchProgressTitle>
                    <BatchProgressHint>
                      {t("batchSendingHint")}
                    </BatchProgressHint>
                  </BatchProgressTop>
                  <BatchList>
                    {(batchSendState.items || []).map((item) => (
                      <BatchListRow key={item.groupId}>
                        <BatchListName>{item.label}</BatchListName>
                        <BatchListStatus status={item.status}>
                          {item.status === "sent" ? t("batchStatusSent") : t("batchStatusPending")}
                        </BatchListStatus>
                      </BatchListRow>
                    ))}
                  </BatchList>
                </BatchProgress>
              )}
            </FormContainer>
          )}
        </Modal.Content>
        <Modal.Actions>
          <CancelButton onClick={() => setShowGenerateModal(false)}>
            {t("modalCancel")}
          </CancelButton>
          <GenerateButton
            onClick={handleGenerateReport}
            disabled={
              !reportName ||
              !selectedCategory ||
              !selectedDebriefSchedule ||
              generating
            }
          >
            {generating ? t("modalGenerating") : t("modalGenerate")}
          </GenerateButton>
        </Modal.Actions>
      </Modal>

      {isAdmin && (
        <ConfirmationDialog
          isOpen={deleteConfirmModal.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title={t("deleteReportTitle")}
          message={t("deleteConfirmMessage", {
            name: deleteConfirmModal.reportName || t("deleteConfirmFallbackName"),
          })}
          confirmText={t("deleteConfirmText")}
          cancelText={t("modalCancel")}
        />
      )}

      {isAdmin && (
        <Modal
          onClose={closeShareModal}
          open={shareModal.isOpen}
          closeIcon={true}
          size="small"
        >
          <Modal.Header>
            <ModalTitle>{t("manageSharingTitle")}</ModalTitle>
          </Modal.Header>
          <Modal.Content>
            <ShareModalBody>
              <ShareSection>
                <ShareSectionTitle>{t("sharedWithSectionTitle")}</ShareSectionTitle>
                {activeSharedWithState?.pending ? (
                  <ShareMutedText>{t("sharedWithLoading")}</ShareMutedText>
                ) : sharedWithList.length === 0 ? (
                  <ShareMutedText>{t("sharedWithNone")}</ShareMutedText>
                ) : (
                  <ShareList>
                    {sharedWithList.map((item) => (
                      <ShareListRow key={item.employee_id}>
                        <SharePerson>
                          <ShareName>{item.employee_name || t("unknownLabel")}</ShareName>
                          <ShareEmail>{item.employee_email || ""}</ShareEmail>
                        </SharePerson>
                        <ShareMeta>
                          <ShareMetaLine>
                            {t("sharedByLabel", { name: item.shared_by_name || t("systemLabel") })}
                          </ShareMetaLine>
                          <ShareMetaLine>
                            {item.shared_at ? new Date(item.shared_at).toLocaleString() : ""}
                          </ShareMetaLine>
                        </ShareMeta>
                        <ShareRemoveButton
                          onClick={() => handleUnshareEmployee(item.employee_id)}
                          disabled={!!activeShareActionState?.pending}
                          title={t("removeShare")}
                        >
                          {t("removeShare")}
                        </ShareRemoveButton>
                      </ShareListRow>
                    ))}
                  </ShareList>
                )}
              </ShareSection>

              <ShareSection>
                <ShareSectionTitle>{t("shareWithUsersTitle")}</ShareSectionTitle>
                <ShareControls>
                  <ShareDropdown
                    multiple
                    search
                    selection
                    fluid
                    placeholder={t("placeholderSelectUsersShort")}
                    options={loginEmployeeOptions}
                    value={shareEmployeeIds}
                    onChange={(e, { value }) => {
                      const next = Array.isArray(value) ? value : value ? [value] : [];
                      setShareEmployeeIds(next);
                    }}
                    disabled={!!activeShareActionState?.pending}
                  />
                  <SharePrimaryButton
                    onClick={handleShareSelected}
                    disabled={!shareEmployeeIds.length || !!activeShareActionState?.pending}
                  >
                    {t("shareAction")}
                  </SharePrimaryButton>
                </ShareControls>
                {activeShareActionState?.data?.message && (
                  <ShareResponse>
                    <ShareResponseTitle>{activeShareActionState.data.message}</ShareResponseTitle>
                    {Array.isArray(activeShareActionState.data.shared_with) && (
                      <ShareMutedText>
                        {t("shareResponseNewlyShared", {
                          count: activeShareActionState.data.shared_with.length,
                        })}
                        {Array.isArray(activeShareActionState.data.already_shared)
                          ? ` • ${t("shareResponseAlreadyShared", {
                              count: activeShareActionState.data.already_shared.length,
                            })}`
                          : ""}
                        {typeof activeShareActionState.data.total_shares === "number"
                          ? ` • ${t("shareResponseTotal", {
                              count: activeShareActionState.data.total_shares,
                            })}`
                          : ""}
                      </ShareMutedText>
                    )}
                  </ShareResponse>
                )}
                {activeShareActionState?.error && (
                  <ShareErrorText>{t("shareUpdateError")}</ShareErrorText>
                )}
              </ShareSection>
            </ShareModalBody>
          </Modal.Content>
          <Modal.Actions>
            <CancelButton onClick={closeShareModal}>{t("close")}</CancelButton>
          </Modal.Actions>
        </Modal>
      )}

      {isAdmin && (
        <Modal
          onClose={closeGroupShareModal}
          open={groupShareModalOpen}
          closeIcon={!groupShareSendState.active}
          size="small"
        >
          <Modal.Header>
            <ModalTitle>{t("groupShareTitle")}</ModalTitle>
          </Modal.Header>
          <Modal.Content>
            <GroupShareBody>
            <FormField>
              <Label>{t("groupShareUserRequired")}</Label>
              <div>
                <ShareDropdown
                  search
                  selection
                  fluid
                  placeholder={t("groupShareSelectUserPlaceholder")}
                  options={loginEmployeeOptions}
                  value={groupShareTargetEmployeeId || ""}
                  onChange={(e, { value }) => setGroupShareTargetEmployeeId(value || null)}
                  disabled={groupShareSendState.active}
                />
              </div>
            </FormField>

            <FormField>
              <Label>{t("groupShareManagersOptional")}</Label>
              <div>
                <ShareDropdown
                  multiple
                  search
                  selection
                  fluid
                  placeholder={t("groupShareSelectManagersPlaceholder")}
                  options={managerGroupOptions}
                  value={groupShareManagerGroupIds}
                  onChange={(e, { value }) => {
                    const next = Array.isArray(value) ? value : value ? [value] : [];
                    setGroupShareManagerGroupIds(next);
                  }}
                  disabled={groupShareSendState.active}
                />
              </div>
              <GroupShareInlineActions>
                <SharePrimaryButton
                  onClick={handleLoadReportsFromManagers}
                  disabled={!groupShareManagerGroupIds.length || groupShareLoading.active || groupShareSendState.active}
                >
                  {t("groupShareLoadReportsFromManagers")}
                </SharePrimaryButton>
              </GroupShareInlineActions>
            </FormField>

            <FormField>
              <Label>{t("groupShareSearchReportsOptional")}</Label>
              <GroupShareSearchRow>
                <GroupShareSearchInput
                  value={groupShareSearchTerm}
                  onChange={(e) => setGroupShareSearchTerm(e.target.value)}
                  placeholder={t("groupShareSearchPlaceholder")}
                  disabled={groupShareSendState.active}
                />
                <SharePrimaryButton
                  onClick={handleSearchReportsForGroupShare}
                  disabled={!groupShareSearchTerm.trim() || groupShareLoading.active || groupShareSendState.active}
                >
                  {t("groupShareSearch")}
                </SharePrimaryButton>
              </GroupShareSearchRow>
              <ShareMutedText>
                {t("groupShareLoadedReports", { count: groupShareAvailableReports.length })}
              </ShareMutedText>
              {groupShareTargetEmployeeId && (
                <ShareMutedText>
                  {t("groupShareCurrentlySharedExplicit", { count: groupShareExistingSharedReportIds.length })}
                </ShareMutedText>
              )}
              {groupShareLoading.active && (
                <ShareMutedText>
                  {groupShareLoading.message}
                  {groupShareLoading.mode === "manager_groups"
                    ? ` (${groupShareLoading.done}/${groupShareLoading.total})`
                    : ""}
                </ShareMutedText>
              )}
              {groupShareExistingSharesScan.active && (
                <ShareMutedText>
                  {groupShareExistingSharesScan.message}
                  {groupShareExistingSharesScan.total
                    ? ` (${groupShareExistingSharesScan.done}/${groupShareExistingSharesScan.total})`
                    : ""}
                </ShareMutedText>
              )}
              {groupShareExistingSharesScan.error && (
                <ShareErrorText>{String(groupShareExistingSharesScan.error)}</ShareErrorText>
              )}
            </FormField>

            <FormField>
              <Label>{t("groupShareSelectReportsRequired")}</Label>
              {groupShareAvailableReports.length === 0 ? (
                <ShareMutedText>
                  {t("groupShareEmptyListHint")}
                </ShareMutedText>
              ) : (
                <GroupShareReportList>
                  {groupShareAvailableReports.map((r) => {
                    const rid = Number(r?.id);
                    const checked = Array.isArray(groupShareSelectedReportIds)
                      ? groupShareSelectedReportIds.includes(rid)
                      : false;
                    return (
                      <GroupShareReportRow
                        key={rid}
                        onClick={() => toggleGroupShareReportSelection(rid)}
                        role="button"
                        tabIndex={0}
                      >
                        <GroupShareCheckbox
                          checked={checked}
                          onChange={() => toggleGroupShareReportSelection(rid)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <GroupShareReportText>
                          <GroupShareReportName title={getReportDisplayName(r)}>
                            {getReportDisplayName(r)}
                          </GroupShareReportName>
                          <GroupShareReportMeta>
                            {getReportTemplateLabel(r)} • {formatDate(r?.created_at)}
                          </GroupShareReportMeta>
                        </GroupShareReportText>
                      </GroupShareReportRow>
                    );
                  })}
                </GroupShareReportList>
              )}
              <ShareMutedText>
                {t("groupShareSelectedCount", { count: groupShareSelectedReportIds.length })}
              </ShareMutedText>
            </FormField>

            {groupShareSendState.active && (
              <BatchProgress>
                <BatchProgressTop>
                  <BatchProgressTitle>
                    {t("batchSendingProgress", {
                      sent: groupShareSendState.sent,
                      total: groupShareSendState.total,
                    })}
                  </BatchProgressTitle>
                  <BatchProgressHint>
                    {t("batchSendingHint")}
                  </BatchProgressHint>
                </BatchProgressTop>
                <BatchList>
                  {(groupShareSendState.items || []).map((item) => (
                    <BatchListRow key={`${item.action || "share"}-${item.reportId}`}>
                      <BatchListName>
                        {(item.action === "unshare"
                          ? t("groupShareActionRemovePrefix")
                          : t("groupShareActionSharePrefix")) + (item.label || "")}
                      </BatchListName>
                      <BatchListStatus status={item.status}>
                        {item.status === "sent" ? t("batchStatusSent") : t("batchStatusPending")}
                      </BatchListStatus>
                    </BatchListRow>
                  ))}
                </BatchList>
              </BatchProgress>
            )}
            </GroupShareBody>
          </Modal.Content>
          <Modal.Actions>
            <CancelButton onClick={closeGroupShareModal} disabled={groupShareSendState.active}>
              {t("modalCancel")}
            </CancelButton>
            <GenerateButton
              onClick={handleGroupShareGivePermission}
              disabled={
                groupShareSendState.active ||
                groupShareExistingSharesScan.active ||
                !groupShareTargetEmployeeId ||
                (groupShareDiff.toShare.length === 0 && groupShareDiff.toUnshare.length === 0)
              }
            >
              {t("applyChanges")}
            </GenerateButton>
          </Modal.Actions>
        </Modal>
      )}
    </Container>
  );
};

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(8px); }
  to { opacity: 1; transform: translateX(0); }
`;

const cascadeIn = keyframes`
  0% { opacity: 0; transform: translateY(10px) scale(0.98); filter: blur(2px); }
  60% { opacity: 1; transform: translateY(0) scale(1.01); filter: blur(0); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Styled Components
const Container = styled.div`
  position: relative;
  padding: 32px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  min-height: 100vh;
  isolation: isolate;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 140%;
    transform: translateX(-50%);
    z-index: -1;
    pointer-events: none;
    background:
      radial-gradient(1200px 500px at 10% -10%, rgba(45, 112, 226, 0.12), transparent 60%),
      radial-gradient(1100px 460px at 110% 10%, rgba(39, 205, 167, 0.16), transparent 55%),
      radial-gradient(900px 380px at 50% -10%, rgba(255, 255, 255, 0.55), transparent 70%);
    opacity: 0.35;
    filter: saturate(100%);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
  gap: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 27px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  letter-spacing: -0.01em;
  flex: 1;
`;

const SegmentedControl = styled.div`
  display: inline-flex;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 10px;
  padding: 4px;
  gap: 0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
  }
`;

const SegmentedTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${(props) => (props.active ? "#111827" : "transparent")};
  color: ${(props) => (props.active ? "#ffffff" : "#64748b")};
  border: none;
  border-radius: ${(props) => {
    if (props.isFirst) return "8px 0 0 8px";
    if (props.isLast) return "0 8px 8px 0";
    return "0";
  }};
  font-size: 14px;
  font-weight: ${(props) => (props.active ? "600" : "500")};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  white-space: nowrap;
  z-index: ${(props) => (props.active ? "1" : "0")};

  &:hover {
    color: ${(props) => (props.active ? "#ffffff" : "#111827")};
  }

  &:active {
    transform: scale(0.97);
  }

  span {
    transition: color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
  }

  svg {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    flex-shrink: 0;
  }

`;

const LibraryIcon = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="6" width="4" height="12" rx="1" fill={active ? "#ffffff" : "#64748b"} opacity={active ? "1" : "0.7"} />
    <rect x="10" y="4" width="4" height="14" rx="1" fill={active ? "#ffffff" : "#64748b"} opacity={active ? "1" : "0.7"} />
    <rect x="16" y="8" width="4" height="10" rx="1" fill={active ? "#ffffff" : "#64748b"} opacity={active ? "1" : "0.7"} />
  </svg>
);

const HistoryIcon = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke={active ? "#ffffff" : "#64748b"} strokeWidth="1.5" fill="none" opacity={active ? "1" : "0.7"} />
    <path d="M12 8v4l3 3" stroke={active ? "#ffffff" : "#64748b"} strokeWidth="1.5" strokeLinecap="round" fill="none" opacity={active ? "1" : "0.7"} />
  </svg>
);

const Section = styled.section`
  margin-bottom: 48px;
  animation: ${slideIn} 0.35s cubic-bezier(0.4, 0, 0.2, 1);
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  animation: ${fadeIn} 1s ease-out 0.2s both;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const AnalyticsCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
  cursor: pointer;
  transition: box-shadow 0.25s ease, transform 0.2s ease, border-color 0.25s ease;
  display: flex;
  flex-direction: column;
  padding: 16px;
  animation: ${cascadeIn} 520ms cubic-bezier(0.16, 1, 0.3, 1) both;
  will-change: transform, opacity;
  opacity: 0;
  min-height: 190px;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(148, 163, 184, 0.32);
    box-shadow: 0 16px 32px rgba(15, 23, 42, 0.12);
  }

  ${props => props.isLoading && `
    pointer-events: none;
    opacity: 0.8;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

const CardTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

const IconBox = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: ${({ bg }) => bg || '#f8fafc'};
  border: 1px solid #eef2f7;
  transition: all 0.25s ease;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.9;
  
  svg {
    width: 22px;
    height: 22px;
    display: block;
  }
`;

const CardBody = styled.div`
  margin-top: 10px;
  flex: 1;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  text-align: left;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 8px 0;
  line-height: 1.5;
  font-weight: 400;
  text-align: left;
`;

const TemplateInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
`;

const InfoItem = styled.span`
  font-size: 12px;
  color: #94a3b8;
`;

const CardFooter = styled.div`
  margin-top: auto;
  padding-top: 20px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
`;

const OpenButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  transition: color 0.18s ease, transform 0.18s ease;

  &:hover {
    color: #0f172a;
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);
    border-radius: 6px;
  }
`;

const ExploreArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M8 16L16 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M9 8h7v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const LoadingSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #94a3b8;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-right: 4px;
`;

// Empty state (matches ProtoType/StaplesReports empty state)
const EmptyWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 120px);
  width: 100%;
`;

const EmptyContent = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const EmptyIcon = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
`;

const EmptyTitle = styled.h2`
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-weight: 700;
  font-size: 22px;
  color: #111827;
  margin: 0 0 8px 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyText = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0 0 20px 0;
`;

const EmptyButton = styled.button`
  padding: 10px 24px;
  background: #2D70E2;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #2563eb;
  }
`;


// Table Components
const TableContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: #f8fafc;
`;

const TableHeaderRow = styled.tr``;

const TableHeaderCell = styled.th`
  padding: 14px 18px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
  white-space: nowrap;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 14px 18px;
  font-size: 14px;
  color: #1e293b;
`;

const TableEmptyCell = styled.td`
  padding: 46px 18px;
  text-align: center;
`;

const ReportNameText = styled.div`
  font-weight: 600;
  color: #1e293b;
`;

const DateText = styled.div`
  color: #64748b;
  font-size: 14px;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) => {
    const status = props.status;
    if (status === 1) return "#e0f2fe"; // New
    if (status === 5) return "#fef3c7"; // Queued for Processing
    return "#f1f5f9"; // Default
  }};
  color: ${(props) => {
    const status = props.status;
    if (status === 1) return "#0369a1"; // New
    if (status === 5) return "#92400e"; // Queued for Processing
    return "#64748b"; // Default
  }};
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const HistoryTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const HistoryTopBarSpacer = styled.div``;

const HistoryTopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const HistorySearchLabel = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #0f172a;
  white-space: nowrap;
`;

const HistorySearchInput = styled.input`
  width: 420px;
  max-width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 10px;
  font-size: 14px;
  color: #1e293b;

  &:focus {
    outline: none;
    border-color: #2D70E2;
    box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
  }
`;

const HistorySearchClearButton = styled.button`
  padding: 10px 12px;
  background: transparent;
  color: #475569;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #f8fafc;
    color: #0f172a;
    border-color: rgba(148, 163, 184, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HistoryTopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ReportsPagination = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ReportsPaginationInfo = styled.div`
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
`;

const ReportsPaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ReportsPaginationButton = styled.button`
  padding: 8px 12px;
  background: transparent;
  color: #1e293b;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: rgba(148, 163, 184, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ReportsPaginationPage = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: #0f172a;
`;

const HistoryTopBarButton = styled.button`
  padding: 10px 14px;
  background: #111827;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.92;
    transform: translateY(-1px);
  }
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  background: transparent;
  color: #64748b;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f8fafc;
    color: #1e293b;
    border-color: rgba(148, 163, 184, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled.button`
  padding: 6px;
  background: transparent;
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #fef2f2;
    border-color: rgba(239, 68, 68, 0.4);
    color: #dc2626;
  }
`;

const ShareModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ShareSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ShareSectionTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
`;

const ShareMutedText = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
`;

const ShareErrorText = styled.div`
  font-size: 13px;
  color: #dc2626;
  font-weight: 600;
`;

const ShareList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ShareListRow = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 1fr) minmax(160px, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 10px;
`;

const SharePerson = styled.div`
  min-width: 0;
`;

const ShareName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ShareEmail = styled.div`
  font-size: 12px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ShareMeta = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ShareMetaLine = styled.div`
  font-size: 12px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ShareRemoveButton = styled.button`
  padding: 6px 10px;
  background: transparent;
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.25);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #fef2f2;
    border-color: rgba(239, 68, 68, 0.45);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ShareControls = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ShareDropdown = styled(Dropdown)`
  &&& {
    flex: 1;
  }
`;

const SharePrimaryButton = styled.button`
  padding: 10px 14px;
  background: #111827;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const PasteEmailsPanel = styled.div`
  margin-top: 10px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 12px;
  padding: 12px;
  background: #ffffff;
`;

const PasteEmailsHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const PasteEmailsTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: #0f172a;
`;

const PasteEmailsActions = styled.div`
  display: flex;
  gap: 10px;
`;

const PasteEmailsSecondaryButton = styled.button`
  padding: 8px 12px;
  background: transparent;
  color: #475569;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #f8fafc;
    color: #0f172a;
    border-color: rgba(148, 163, 184, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PasteEmailsPrimaryButton = styled.button`
  padding: 8px 12px;
  background: #2D70E2;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const PasteEmailsHint = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
`;

const PasteEmailsTextarea = styled.textarea`
  margin-top: 10px;
  width: 100%;
  min-height: 110px;
  resize: vertical;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 10px;
  font-size: 13px;
  color: #0f172a;
  font-family: inherit;
  line-height: 1.4;

  &:focus {
    outline: none;
    border-color: #2D70E2;
    box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f8fafc;
  }
`;

const PasteEmailsResultBox = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid
    ${(props) => (props.hasError ? "rgba(239, 68, 68, 0.35)" : "rgba(148, 163, 184, 0.22)")};
  background: ${(props) => (props.hasError ? "#fef2f2" : "#f8fafc")};
`;

const PasteEmailsResultLine = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
`;

const PasteEmailsUnmatchedList = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PasteEmailsChip = styled.div`
  font-size: 12px;
  color: #0f172a;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: #ffffff;
`;

const PasteEmailsMoreText = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 700;
  padding: 4px 2px;
`;

const GroupShareBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GroupShareInlineActions = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
`;

const GroupShareSearchRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const GroupShareSearchInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 10px;
  font-size: 14px;
  color: #1e293b;

  &:focus {
    outline: none;
    border-color: #2D70E2;
    box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
  }
`;

const GroupShareReportList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 280px;
  overflow: auto;
  padding-right: 4px;
`;

const GroupShareReportRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease;

  &:hover {
    background: #f8fafc;
    border-color: rgba(148, 163, 184, 0.3);
  }
`;

const GroupShareCheckbox = styled.input.attrs({ type: "checkbox" })`
  margin-top: 3px;
`;

const GroupShareReportText = styled.div`
  min-width: 0;
  flex: 1;
`;

const GroupShareReportName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GroupShareReportMeta = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ShareResponse = styled.div`
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.18);
`;

const ShareResponseTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
`;

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);

// Confirmation Dialog Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`;

const ConfirmationContent = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 480px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ConfirmationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
`;

const ConfirmationTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: #1e293b;
  }
`;

const ConfirmationBody = styled.div`
  padding: 24px;
`;

const ConfirmationMessage = styled.p`
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
`;

const ConfirmationActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 20px 24px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
`;

const ConfirmButton = styled.button`
  padding: 10px 20px;
  background: #ef4444;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #dc2626;
  }
`;

const ConfirmationDialog = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Delete", cancelText = "Cancel" }) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ConfirmationContent onClick={e => e.stopPropagation()}>
        <ConfirmationHeader>
          <ConfirmationTitle>{title}</ConfirmationTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ConfirmationHeader>
        
        <ConfirmationBody>
          <ConfirmationMessage>{message}</ConfirmationMessage>
        </ConfirmationBody>
        
        <ConfirmationActions>
          <CancelButton onClick={onClose}>
            {cancelText}
          </CancelButton>
          <ConfirmButton onClick={onConfirm}>
            {confirmText}
          </ConfirmButton>
        </ConfirmationActions>
      </ConfirmationContent>
    </ModalOverlay>
  );
};

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #2D70E2;
    box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #2D70E2;
    box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
  }
`;

const ModalDropdown = styled(Dropdown)`
  &&& {
    width: 100%;
  }
`;

const BatchProgress = styled.div`
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 10px;
  padding: 12px;
  background: #ffffff;
`;

const BatchProgressTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
`;

const BatchProgressTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
`;

const BatchProgressHint = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const BatchList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 180px;
  overflow: auto;
`;

const BatchListRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 8px;
`;

const BatchListName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BatchListStatus = styled.div`
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${(props) => (props.status === "sent" ? "#ecfdf5" : "#f8fafc")};
  color: ${(props) => (props.status === "sent" ? "#047857" : "#64748b")};
  border: 1px solid
    ${(props) =>
      props.status === "sent" ? "rgba(16, 185, 129, 0.25)" : "rgba(148, 163, 184, 0.24)"};
  flex: 0 0 auto;
`;

const TemplateDisplay = styled.div`
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  color: #64748b;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    color: #1e293b;
  }
`;

const GenerateButton = styled.button`
  padding: 10px 20px;
  background: #2D70E2;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default AutomatedReports;

